/**
 * Admin Waitlist — read-only view of the department-wide waitlist queue.
 *
 * Reads `GET /api/waitlist` (already scoped server-side to non-student roles).
 * Waitlist entries auto-fulfill when an admin approves a matching return, so
 * this page is intentionally a passive monitor: no create / delete controls.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Search } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function WaitlistPage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(endpoints.waitlist.list);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('waitlist', e);
      showToast && showToast('Failed to load waitlist', 'err');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = `${r.student_name || ''} ${r.component_name || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  const componentsWaiting = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.component_id, (m.get(r.component_id) || 0) + r.quantity));
    return m.size;
  }, [rows]);

  return (
    <div data-testid="ap-waitlist-page">
      <div className="ap-eyebrow">Faculty Console</div>
      <h1 className="ap-page-title">Waitlist</h1>
      <p className="ap-page-lede">
        Department-wide queue of students awaiting stock that isn't available today.
        Entries auto-fulfil as soon as an approved return replenishes the pool.
      </p>

      <div className="ap-ind-row" role="group" aria-label="Waitlist summary">
        <div className="ap-ind" data-testid="ap-waitlist-count-entries">
          <div className="ap-ind-label"><Clock size={11} style={{ verticalAlign: -2 }} /> Waiting entries</div>
          <div className="ap-ind-value">{rows.length}</div>
          <div className="ap-ind-hint">Distinct student × component pairs</div>
        </div>
        <div className="ap-ind" data-testid="ap-waitlist-count-components">
          <div className="ap-ind-label">Components with a queue</div>
          <div className="ap-ind-value">{componentsWaiting}</div>
          <div className="ap-ind-hint">Auto-fulfilment on next return</div>
        </div>
      </div>

      <div className="ap-mt-6" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: 420 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 12, opacity: 0.5 }} />
          <input
            className="ap-input"
            style={{ paddingLeft: 30 }}
            placeholder="Search student or component…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ap-waitlist-search"
          />
        </div>
      </div>

      <div className="ap-mt-6">
        {loading ? (
          <div className="ap-empty">Loading waitlist…</div>
        ) : filtered.length === 0 ? (
          <div className="ap-empty" data-testid="ap-waitlist-empty">
            {rows.length === 0
              ? 'No one on the waitlist. Every request currently in stock.'
              : 'No entries match your search.'}
          </div>
        ) : (
          <div className="ap-table-wrap" data-testid="ap-waitlist-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Component</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Position</th>
                  <th>Waiting since</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} data-testid={`ap-waitlist-row-${e.id}`}>
                    <td>{e.student_name || '—'}</td>
                    <td>{e.component_name || '—'}</td>
                    <td style={{ textAlign: 'right' }} className="cp-mono-num">{e.quantity}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ background: 'var(--diis-canvas-alt)', border: '1px solid var(--diis-hairline)', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontFamily: 'var(--diis-font-mono)' }}>
                        #{e.position}
                      </span>
                    </td>
                    <td>{fmt(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
